import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiSearch,
  FiDownload,
  FiRefreshCw,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getSelfActivateWallets } from "../../store/slices/profile";

const SelfActivationWallet = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const {
    selfActivateWallets = [],
    selfActivateWalletsLoading = false,
    selfActivateWalletsCount = 0,
    selfActivateWalletsPagination = {},
    error,
  } = useSelector((state) => state.profile);

  // Debounce search to avoid making an API request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);
  const {
    selfActivateWalletsTotalAmountAvailable = 0,
  } = useSelector((state) => state.profile);

  // Fetch wallet history.
  useEffect(() => {
    dispatch(
      getSelfActivateWallets({
        page,
        limit,
        search,
      })
    );
  }, [dispatch, page, limit, search]);

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "-";

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",

    });
  };

  const totalPages =
    Number(selfActivateWalletsPagination?.totalPages) ||
    Math.ceil(selfActivateWalletsCount / limit);

  const displayStart =
    selfActivateWalletsCount > 0
      ? (page - 1) * limit + 1
      : 0;

  const displayEnd = Math.min(
    page * limit,
    selfActivateWalletsCount
  );

  const handlePrevious = () => {
    if (page > 1 && !selfActivateWalletsLoading) {
      setPage((previous) => previous - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages && !selfActivateWalletsLoading) {
      setPage((previous) => previous + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page &&
      !selfActivateWalletsLoading
    ) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        page - 1,
        page,
        page + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  // The thunk returns Axios response.data:
  // { status, data: { selfActivateWallets, pagination, ... } }
  const extractWallets = (response) => {
    const data = response?.data ?? response;

    return Array.isArray(data?.selfActivateWallets)
      ? data.selfActivateWallets
      : [];
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      // Request all matching records for export.
      // The backend must support the supplied limit.
      const response = await dispatch(
        getSelfActivateWallets({
          page: 1,
          limit: Math.max(selfActivateWalletsCount, 1),
          search,
        })
      ).unwrap();

      const exportList = extractWallets(response);

      if (exportList.length === 0) {
        window.alert("No records available to download.");
        return;
      }

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(16);
      doc.text("Self Activation Wallet History", 14, 15);

      doc.setFontSize(9);
      doc.text(
        `Total records exported: ${exportList.length}`,
        14,
        20
      );

      const tableData = exportList.map((item, index) => {
        const user =
          item.userId && typeof item.userId === "object"
            ? item.userId
            : {};

        return [
          index + 1,
          user.name || "-",
          user.userId || "-",
          item.transactionId || "-",
          item.amount ?? 0,
          formatDate(item.createdAt),
          item.status || "-",
        ];
      });

      autoTable(doc, {
        startY: 25,
        head: [
          [
            "#",
            "Name",
            "User ID",
            "Transaction ID",
            "Amount",

            "Date",
            "Status",
          ],
        ],
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak",
        },
        headStyles: {
          fontSize: 7,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 9 },
          1: { cellWidth: 30 },
          2: { cellWidth: 22 },
          3: { cellWidth: 27 },
          4: { cellWidth: 16 },
          5: { cellWidth: 45 },
          6: { cellWidth: 22 },
          7: { cellWidth: 22 },
          8: { cellWidth: 30 },
          9: { cellWidth: 18 },
        },
      });

      doc.save("self-activation-wallet-history.pdf");
    } catch (err) {
      console.error("PDF download failed:", err);

      window.alert(
        typeof err === "string"
          ? err
          : "Failed to download PDF. Please try again."
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(
      getSelfActivateWallets({
        page,
        limit,
        search,
      })
    );
  };

  return (
    <div className="gi-referral-page">
      {/* Breadcrumb */}
      <div className="gi-breadcrumb">
        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>Self Activation Wallets</span>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>Wallet History</span>
      </div>

      <section className="gi-referral-table-card">
        {/* Filters */}
        <div className="gi-users-filter">
          <div className="gi-users-page-size me-2">
            <label htmlFor="pageSize">Show</label>

            <select
              id="pageSize"
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              disabled={selfActivateWalletsLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>per page</span>
          </div>

          <div className="gi-users-search me-2">
            <FiSearch />

            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, user ID or transaction ID..."
            />
          </div>

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleRefresh}
            disabled={selfActivateWalletsLoading}
          >
            <FiRefreshCw />

            <span>
              {selfActivateWalletsLoading
                ? "Loading..."
                : "Refresh"}
            </span>
          </button>

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              selfActivateWalletsLoading ||
              selfActivateWalletsCount === 0
            }
          >
            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>
          </button>
        </div>

        {/* Summary */}
        <div className="d-flex flex-wrap gap-3 p-3">

          <div className="p-3 border rounded">
            <div className="text-muted">Total Amount Available</div>
            <strong>
              {Number(
                selfActivateWalletsTotalAmountAvailable ?? 0
              ).toLocaleString("en-IN")}
            </strong>
          </div>
        </div>

        {/* Table */}
        <div className="gi-referral-table-wrapper">
          <table className="gi-referral-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th className="text-center">User ID</th>
                <th className="text-center">Transaction ID</th>
                <th className="text-center">Amount</th>

                <th className="text-center">Date</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>

            <tbody>
              {selfActivateWalletsLoading && (
                <tr>
                  <td colSpan={10} className="gi-table-message">
                    Loading self-activation wallet history...
                  </td>
                </tr>
              )}

              {!selfActivateWalletsLoading && error && (
                <tr>
                  <td colSpan={10} className="gi-table-message">
                    {error}
                  </td>
                </tr>
              )}

              {!selfActivateWalletsLoading &&
                !error &&
                selfActivateWallets.length === 0 && (
                  <tr>
                    <td colSpan={10} className="gi-table-message">
                      No self-activation wallet records found.
                    </td>
                  </tr>
                )}

              {!selfActivateWalletsLoading &&
                !error &&
                selfActivateWallets.map((item, index) => {
                  const user =
                    item.userId &&
                      typeof item.userId === "object"
                      ? item.userId
                      : {};

                  const status = item.status || "-";

                  return (
                    <tr key={item._id || index}>
                      <td>
                        {String(
                          (page - 1) * limit + index + 1
                        ).padStart(2, "0")}
                      </td>

                      <td>{user.name || "-"}</td>

                      <td className="text-center">
                        {user.userId || "-"}
                      </td>

                      <td className="text-center">
                        {item.transactionId || "-"}
                      </td>

                      <td className="text-center">
                        <strong className="gi-payment-amount">
                          {Number(item.amount ?? 0).toLocaleString(
                            "en-IN"
                          )}
                        </strong>
                      </td>


                      <td className="text-center">
                        <span className="gi-date-text">
                          {formatDate(item.createdAt)}
                        </span>
                      </td>

                      <td className="text-center">
                        <span
                          className={`gi-status-badge ${String(
                            status
                          ).toLowerCase()}`}
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* Pagination */}
          {selfActivateWalletsCount > 0 && (
            <div className="gi-users-pagination">
              <div className="gi-pagination-info">
                Showing <strong>{displayStart}</strong> to{" "}
                <strong>{displayEnd}</strong> of{" "}
                <strong>{selfActivateWalletsCount}</strong> records
              </div>

              <div className="gi-pagination-buttons">
                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handlePrevious}
                  disabled={
                    page === 1 || selfActivateWalletsLoading
                  }
                  aria-label="Previous page"
                >
                  <FiChevronLeft />
                </button>

                {getPageNumbers().map((pageNumber, index) =>
                  pageNumber === "..." ? (
                    <span
                      key={`dots-${index}`}
                      className="gi-pagination-dots"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={pageNumber}
                      className={`gi-pagination-number ${page === pageNumber ? "active" : ""
                        }`}
                      onClick={() => handlePageChange(pageNumber)}
                      disabled={selfActivateWalletsLoading}
                    >
                      {pageNumber}
                    </button>
                  )
                )}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handleNext}
                  disabled={
                    page >= totalPages ||
                    selfActivateWalletsLoading
                  }
                  aria-label="Next page"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SelfActivationWallet;
